import type { MeResponse } from "@/features/auth/types";
import { hasFeature } from "@/lib/permissions/can";

/**
 * Module « Assurances et tiers payant » (ADR 0063). Actif par défaut : le
 * serveur le place dans `features` tant que personne ne l'éteint. Éteint pour
 * la clinique active : menus, onglets, champs de saisie, mode de paiement,
 * widgets et rapports d'assurance sont masqués. Une part assurance DÉJÀ
 * portée par un document reste affichée sur sa page (les pages de détail ne
 * la montrent que si elle existe).
 */
export function usesInsurance(me: MeResponse | null | undefined): boolean {
  return hasFeature(me ?? null, "insurance");
}

/** Modes de paiement proposés : sans le module, pas de mode « Assurance ». */
export function insuranceAwareMethods<T extends { value: string }>(
  me: MeResponse | null | undefined,
  methods: readonly T[],
): T[] {
  return usesInsurance(me)
    ? [...methods]
    : methods.filter((m) => m.value !== "insurance");
}

/** Lignes « par mode de paiement » (règlements du jour) : sans le module, la
 *  ligne « Assurance » n'est pas affichée. */
export function insuranceAwareMethodRows<T extends { method: string }>(
  me: MeResponse | null | undefined,
  rows: readonly T[],
): T[] {
  return usesInsurance(me)
    ? [...rows]
    : rows.filter((r) => r.method !== "insurance");
}

/** Colonnes de listes propres au tiers payant (factures et devis). */
export const INSURANCE_COLUMN_HEADERS: readonly string[] = [
  "Assureur",
  "Part assurance",
  "Part patient",
  "Payé assurance",
  "Reste assurance",
];

/** Retire les colonnes de tiers payant quand le module est éteint. */
export function insuranceAwareColumns<T extends { header: string }>(
  me: MeResponse | null | undefined,
  columns: readonly T[],
): T[] {
  if (usesInsurance(me)) return [...columns];
  return columns.filter((c) => !INSURANCE_COLUMN_HEADERS.includes(c.header));
}
